import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const formatKES = (amount) => {
  const num = parseFloat(amount) || 0;
  return `KES ${num?.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const STATUS_LABELS = {
  draft: 'DRAFT',
  sent: 'SENT',
  paid: 'PAID',
  partially_paid: 'PARTIALLY PAID',
  overdue: 'OVERDUE',
  cancelled: 'CANCELLED'
};

const STATUS_COLORS = {
  draft: [100, 116, 139],
  sent: [59, 130, 246],
  paid: [16, 185, 129],
  partially_paid: [245, 158, 11],
  overdue: [239, 68, 68],
  cancelled: [100, 116, 139]
};

export const generateInvoicePDF = (invoice) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  const pageHeight = doc?.internal?.pageSize?.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;

  // ── Color Palette ──────────────────────────────────────────────────────────
  const primaryColor = [30, 64, 175];   // #1e40af (LawDesk blue)
  const darkText = [17, 24, 39];        // #111827
  const mutedText = [107, 114, 128];    // #6b7280
  const lightBg = [248, 250, 252];      // #f8fafc
  const borderColor = [226, 232, 240];  // #e2e8f0

  let y = margin;

  // ── Header Background ──────────────────────────────────────────────────────
  doc?.setFillColor(...primaryColor);
  doc?.rect(0, 0, pageWidth, 42, 'F');

  // ── Logo / Firm Name ───────────────────────────────────────────────────────
  doc?.setFillColor(255, 255, 255);
  doc?.roundedRect(margin, 10, 10, 10, 2, 2, 'F');
  doc?.setFillColor(...primaryColor);
  // Scale icon placeholder
  doc?.setDrawColor(255, 255, 255);
  doc?.setLineWidth(0.5);
  doc?.line(margin + 3, 17, margin + 7, 17);
  doc?.line(margin + 5, 13, margin + 5, 17);
  doc?.line(margin + 3, 13, margin + 7, 13);

  doc?.setFont('helvetica', 'bold');
  doc?.setFontSize(18);
  doc?.setTextColor(255, 255, 255);
  doc?.text('LawDesk KE', margin + 13, 18);

  doc?.setFont('helvetica', 'normal');
  doc?.setFontSize(8);
  doc?.setTextColor(200, 210, 240);
  doc?.text('Legal Practice Management', margin + 13, 23);
  doc?.text('Nairobi, Kenya  |  VAT PIN: P051234567X', margin + 13, 28);
  doc?.text('info@lawdesk.ke  |  +254 700 000 000', margin + 13, 33);

  // ── INVOICE label (right side of header) ───────────────────────────────────
  doc?.setFont('helvetica', 'bold');
  doc?.setFontSize(26);
  doc?.setTextColor(255, 255, 255);
  doc?.text('INVOICE', pageWidth - margin, 22, { align: 'right' });

  doc?.setFont('helvetica', 'normal');
  doc?.setFontSize(10);
  doc?.setTextColor(200, 210, 240);
  doc?.text(invoice?.invoiceNumber || '', pageWidth - margin, 30, { align: 'right' });

  y = 52;

  // ── Status Badge ───────────────────────────────────────────────────────────
  const statusKey = invoice?.status || 'draft';
  const statusLabel = STATUS_LABELS?.[statusKey] || statusKey?.toUpperCase();
  const statusColor = STATUS_COLORS?.[statusKey] || [100, 116, 139];
  const badgeWidth = doc?.getTextWidth(statusLabel) + 8;
  doc?.setFillColor(...statusColor);
  doc?.roundedRect(margin, y - 5, badgeWidth, 7, 1.5, 1.5, 'F');
  doc?.setFont('helvetica', 'bold');
  doc?.setFontSize(7);
  doc?.setTextColor(255, 255, 255);
  doc?.text(statusLabel, margin + 4, y);

  // ── Dates (right side) ─────────────────────────────────────────────────────
  doc?.setFont('helvetica', 'normal');
  doc?.setFontSize(9);
  doc?.setTextColor(...mutedText);
  const issueDate = invoice?.issueDate ? new Date(invoice.issueDate)?.toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const dueDate = invoice?.dueDate ? new Date(invoice.dueDate)?.toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  doc?.text(`Issue Date:`, pageWidth - margin - 50, y - 2, { align: 'left' });
  doc?.setTextColor(...darkText);
  doc?.setFont('helvetica', 'bold');
  doc?.text(issueDate, pageWidth - margin, y - 2, { align: 'right' });
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(...mutedText);
  doc?.text(`Due Date:`, pageWidth - margin - 50, y + 4, { align: 'left' });
  doc?.setTextColor(...darkText);
  doc?.setFont('helvetica', 'bold');
  doc?.text(dueDate, pageWidth - margin, y + 4, { align: 'right' });

  y += 14;

  // ── Divider ────────────────────────────────────────────────────────────────
  doc?.setDrawColor(...borderColor);
  doc?.setLineWidth(0.3);
  doc?.line(margin, y, pageWidth - margin, y);
  y += 8;

  // ── Bill To / Matter ───────────────────────────────────────────────────────
  const colW = (contentWidth - 6) / 2;

  // Bill To box
  doc?.setFillColor(...lightBg);
  doc?.roundedRect(margin, y, colW, 30, 2, 2, 'F');
  doc?.setFont('helvetica', 'bold');
  doc?.setFontSize(7);
  doc?.setTextColor(...mutedText);
  doc?.text('BILL TO', margin + 4, y + 6);
  doc?.setFont('helvetica', 'bold');
  doc?.setFontSize(10);
  doc?.setTextColor(...darkText);
  doc?.text(invoice?.client?.fullName || '—', margin + 4, y + 13);
  doc?.setFont('helvetica', 'normal');
  doc?.setFontSize(8.5);
  doc?.setTextColor(...mutedText);
  if (invoice?.client?.email) doc?.text(invoice?.client?.email, margin + 4, y + 19);
  if (invoice?.client?.phone) doc?.text(invoice?.client?.phone, margin + 4, y + 25);

  // Matter box
  const col2X = margin + colW + 6;
  doc?.setFillColor(...lightBg);
  doc?.roundedRect(col2X, y, colW, 30, 2, 2, 'F');
  doc?.setFont('helvetica', 'bold');
  doc?.setFontSize(7);
  doc?.setTextColor(...mutedText);
  doc?.text('MATTER / CASE', col2X + 4, y + 6);
  doc?.setFont('helvetica', 'bold');
  doc?.setFontSize(10);
  doc?.setTextColor(...darkText);
  const caseTitle = invoice?.caseTitle || '—';
  const caseTitleLines = doc?.splitTextToSize(caseTitle, colW - 8);
  doc?.text(caseTitleLines?.[0], col2X + 4, y + 13);
  if (caseTitleLines?.[1]) doc?.text(caseTitleLines?.[1], col2X + 4, y + 19);
  if (invoice?.description) {
    doc?.setFont('helvetica', 'normal');
    doc?.setFontSize(8);
    doc?.setTextColor(...mutedText);
    const descLines = doc?.splitTextToSize(invoice?.description, colW - 8);
    doc?.text(descLines?.[0], col2X + 4, y + 25);
  }

  y += 38;

  // ── Line Items Table ───────────────────────────────────────────────────────
  const lineItems = invoice?.lineItems || [];
  const tableBody = lineItems?.map(item => [
    item?.description || '',
    item?.itemType === 'time' ? 'Time' : item?.itemType === 'fixed_fee' ? 'Fixed Fee' : 'Disbursement',
    String(item?.quantity ?? ''),
    formatKES(item?.rate),
    formatKES(item?.amount)
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Description', 'Type', 'Qty', 'Rate (KES)', 'Amount (KES)']],
    body: tableBody,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 8.5,
      cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
      textColor: darkText,
      lineColor: borderColor,
      lineWidth: 0.2
    },
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8
    },
    alternateRowStyles: {
      fillColor: lightBg
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 28, halign: 'center' },
      2: { cellWidth: 14, halign: 'right' },
      3: { cellWidth: 32, halign: 'right' },
      4: { cellWidth: 36, halign: 'right', fontStyle: 'bold' }
    },
    didDrawPage: () => {}
  });

  y = doc?.lastAutoTable?.finalY + 8;

  // ── Totals ─────────────────────────────────────────────────────────────────
  const totalsX = pageWidth - margin - 70;
  const totalsW = 70;

  // Subtotal row
  doc?.setFont('helvetica', 'normal');
  doc?.setFontSize(9);
  doc?.setTextColor(...mutedText);
  doc?.text('Subtotal', totalsX, y);
  doc?.setTextColor(...darkText);
  doc?.text(formatKES(invoice?.subtotal), pageWidth - margin, y, { align: 'right' });
  y += 7;

  // VAT row
  doc?.setTextColor(...mutedText);
  doc?.text(`VAT (${invoice?.vatRate || 16}%)`, totalsX, y);
  doc?.setTextColor(...darkText);
  doc?.text(formatKES(invoice?.vatAmount), pageWidth - margin, y, { align: 'right' });
  y += 2;

  // Total divider
  doc?.setDrawColor(...borderColor);
  doc?.setLineWidth(0.3);
  doc?.line(totalsX, y + 1, pageWidth - margin, y + 1);
  y += 6;

  // Total row (highlighted)
  doc?.setFillColor(...primaryColor);
  doc?.roundedRect(totalsX - 4, y - 4, totalsW + 4, 10, 1.5, 1.5, 'F');
  doc?.setFont('helvetica', 'bold');
  doc?.setFontSize(10);
  doc?.setTextColor(255, 255, 255);
  doc?.text('TOTAL (KES)', totalsX, y + 2);
  doc?.text(formatKES(invoice?.total), pageWidth - margin - 1, y + 2, { align: 'right' });
  y += 14;

  // Paid / Balance rows (if applicable)
  if (parseFloat(invoice?.paidAmount) > 0) {
    doc?.setFont('helvetica', 'normal');
    doc?.setFontSize(9);
    doc?.setTextColor(16, 185, 129);
    doc?.text('Amount Paid', totalsX, y);
    doc?.text(`- ${formatKES(invoice?.paidAmount)}`, pageWidth - margin, y, { align: 'right' });
    y += 7;

    doc?.setFont('helvetica', 'bold');
    doc?.setFontSize(9.5);
    doc?.setTextColor(245, 158, 11);
    doc?.text('Balance Due', totalsX, y);
    doc?.text(formatKES(invoice?.remainingBalance), pageWidth - margin, y, { align: 'right' });
    y += 8;
  }

  // ── Notes / Payment Terms ──────────────────────────────────────────────────
  if (invoice?.notes) {
    y += 4;
    doc?.setFillColor(...lightBg);
    doc?.roundedRect(margin, y, contentWidth, 20, 2, 2, 'F');
    doc?.setFont('helvetica', 'bold');
    doc?.setFontSize(7.5);
    doc?.setTextColor(...mutedText);
    doc?.text('NOTES & PAYMENT TERMS', margin + 4, y + 6);
    doc?.setFont('helvetica', 'normal');
    doc?.setFontSize(8.5);
    doc?.setTextColor(...darkText);
    const noteLines = doc?.splitTextToSize(invoice?.notes, contentWidth - 8);
    doc?.text(noteLines?.slice(0, 2), margin + 4, y + 12);
    y += 26;
  }

  // ── Payment Instructions ───────────────────────────────────────────────────
  y += 4;
  doc?.setFillColor(239, 246, 255); // light blue
  doc?.roundedRect(margin, y, contentWidth, 26, 2, 2, 'F');
  doc?.setFont('helvetica', 'bold');
  doc?.setFontSize(8);
  doc?.setTextColor(...primaryColor);
  doc?.text('PAYMENT INSTRUCTIONS', margin + 4, y + 7);
  doc?.setFont('helvetica', 'normal');
  doc?.setFontSize(8);
  doc?.setTextColor(...darkText);
  doc?.text('M-Pesa Paybill: 123456  |  Account: ' + (invoice?.invoiceNumber || ''), margin + 4, y + 14);
  doc?.text('Bank Transfer: Equity Bank  |  A/C: 0123456789  |  Branch: Nairobi CBD', margin + 4, y + 20);

  // ── Footer ─────────────────────────────────────────────────────────────────
  const footerY = pageHeight - 14;
  doc?.setDrawColor(...borderColor);
  doc?.setLineWidth(0.3);
  doc?.line(margin, footerY - 4, pageWidth - margin, footerY - 4);
  doc?.setFont('helvetica', 'normal');
  doc?.setFontSize(7.5);
  doc?.setTextColor(...mutedText);
  doc?.text(
    'This invoice is issued in compliance with Kenya Revenue Authority (KRA) tax regulations. VAT Registration: P051234567X | All amounts in Kenya Shillings (KES)',
    pageWidth / 2,
    footerY,
    { align: 'center' }
  );

  // ── Save ───────────────────────────────────────────────────────────────────
  const fileName = `${invoice?.invoiceNumber || 'invoice'}_LawDeskKE.pdf`;
  doc?.save(fileName);
};

export default generateInvoicePDF;
